import React from 'react';
import { toast } from 'sonner';
import { Megaphone, Download, Calendar } from 'lucide-react';
import { supabase } from '../../backend/services/supabase';
import { useCampaigns } from '../hooks/useCampaigns';
import CampaignCard from '../components/CampaignCard';
import CampaignAssociations from '../components/campaigns/CampaignAssociations';
import type { Campaign } from '../../shared/types/entities';

type FilterState = {
  status: string;
  minCTR: string;
  dateFrom: string;
  dateTo: string;
  search: string;
};

type SortState = {
  field: 'name' | 'status' | 'spend' | 'ctr';
  order: 'asc' | 'desc';
};

export const Campaigns: React.FC = () => {
  console.debug('Campaigns component rendered');
  
  const [filterState, setFilterState] = React.useState<FilterState>({
    status: '',
    minCTR: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });
  const [sortState, setSortState] = React.useState<SortState>({
    field: 'spend',
    order: 'desc',
  });
  const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [lastSyncTime, setLastSyncTime] = React.useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = React.useState<Campaign | null>(null);
  const [showHistoricalImport, setShowHistoricalImport] = React.useState(false);
  const [historicalImportForm, setHistoricalImportForm] = React.useState({
    startDate: '',
    endDate: '',
    includeInsights: true
  });
  const campaignsPerPage = 12;

  console.debug('About to call useCampaigns with filter:', {
    status: filterState.status || undefined,
    search: filterState.search || undefined,
  });
  
  const { campaigns, refetch, loading: campaignsLoading, error: campaignsError } = useCampaigns({
    status: (filterState.status as 'draft' | 'active' | 'paused' | 'completed') || undefined,
    search: filterState.search || undefined,
  });
  
  console.debug('useCampaigns returned:', { 
    campaignsCount: campaigns.length, 
    loading: campaignsLoading, 
    error: campaignsError 
  });

  // Calculate summary metrics for dashboard overview
  const summary = React.useMemo(() => {
    const totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
    const totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions || 0), 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
    const totalConversions = campaigns.reduce((sum, c) => sum + (c.conversions || 0), 0);
    const averageCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    return {
      spend: totalSpend,
      impressions: totalImpressions,
      clicks: totalClicks,
      ctr: averageCTR,
      conversions: totalConversions,
    };
  }, [campaigns]);

  // Debugging logs for development
  React.useEffect(() => {
    console.debug('Campaigns data loaded:', campaigns.length, 'campaigns');
    console.debug('Summary metrics calculated:', summary);
    console.debug('Filter state updated:', filterState);
    console.debug('Sort state updated:', sortState);
  }, [campaigns, summary, filterState, sortState]);

  // Fetch last sync time (mock for now, replace with actual API call if available)
  React.useEffect(() => {
    const fetchLastSyncTime = async () => {
      // Mocking last sync time for demonstration; replace with actual API or local storage check
      const now = new Date().toISOString();
      setLastSyncTime(now);
    };
    fetchLastSyncTime();
  }, []);

  // Apply client-side filters and sorting
  const filteredCampaigns = React.useMemo(() => {
    let result = [...campaigns];

    // Apply additional client-side filters not handled by hook
    if (filterState.minCTR) {
      const minCTRValue = parseFloat(filterState.minCTR);
      if (!isNaN(minCTRValue)) {
        result = result.filter(c => (c.ctr || 0) >= minCTRValue);
      }
    }

    // Apply date range filter if provided
    if (filterState.dateFrom) {
      const fromDate = new Date(filterState.dateFrom);
      result = result.filter(c => c.created_at ? new Date(c.created_at) >= fromDate : true);
    }
    if (filterState.dateTo) {
      const toDate = new Date(filterState.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      result = result.filter(c => c.created_at ? new Date(c.created_at) <= toDate : true);
    }

    // Apply sorting
    result.sort((a, b) => {
      const field = sortState.field;
      const multiplier = sortState.order === 'asc' ? 1 : -1;
      if (field === 'name') {
        return a.name.localeCompare(b.name) * multiplier;
      } else if (field === 'status') {
        return (a.status || '').localeCompare(b.status || '') * multiplier;
      } else if (field === 'spend') {
        return ((a.spend || 0) - (b.spend || 0)) * multiplier;
      } else if (field === 'ctr') {
        return ((a.ctr || 0) - (b.ctr || 0)) * multiplier;
      }
      return 0;
    });

    return result;
  }, [campaigns, filterState, sortState]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCampaigns.length / campaignsPerPage);
  const paginatedCampaigns = filteredCampaigns.slice((currentPage - 1) * campaignsPerPage, currentPage * campaignsPerPage);

  const resetFilters = () => {
    setFilterState({
      status: '',
      minCTR: '',
      dateFrom: '',
      dateTo: '',
      search: '',
    });
    setSortState({
      field: 'spend',
      order: 'desc',
    });
    setCurrentPage(1);
    console.debug('Filters and sorting reset');
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilterState(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSortChange = (field: 'name' | 'status' | 'spend' | 'ctr') => {
    setSortState(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const syncWithMeta = async () => {
    console.debug('syncWithMeta called');
    try {
      setLoading(true);
      console.debug('Getting Supabase session...');
      const session = await supabase.auth.getSession();
      console.debug('Session:', session.data.session ? 'exists' : 'null');
      
      console.debug('Making sync request...');
      const resp = await fetch('/api/meta/campaigns/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        },
        body: JSON.stringify({})
      });
      
      console.debug('Sync response status:', resp.status, resp.statusText);
      const json = await resp.json();
      console.debug('Sync response data:', JSON.stringify(json, null, 2));
      
      if (!resp.ok) throw new Error(json?.error || 'sync_failed');
      toast.success(`Synced Meta campaigns: ${json.total_external} campaigns imported`);
      await refetch();
      setLastSyncTime(new Date().toISOString()); // Update sync time on successful sync
      console.debug('Sync with Meta successful:', json);
    } catch (e: any) {
      console.error('Sync with Meta failed:', e);
      toast.error(`Sync failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const syncNow = async () => {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const resp = await fetch('/api/jobs/meta/sync-spend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        }
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || 'sync_failed');
      toast.success('Meta spend sync started');
      setLastSyncTime(new Date().toISOString()); // Update sync time on successful sync
      console.debug('Meta spend sync started:', json);
    } catch (e: any) {
      toast.error(`Sync failed: ${e?.message || e}`);
      console.error('Meta spend sync failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      await refetch();
      setLastSyncTime(new Date().toISOString());
      toast.success('Campaign data refreshed');
    } catch (e: any) {
      toast.error(`Refresh failed: ${e?.message || e}`);
      console.error('Data refresh failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleHistoricalImport = async () => {
    if (!historicalImportForm.startDate || !historicalImportForm.endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const response = await fetch('/api/meta/historical-import-bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        },
        body: JSON.stringify({
          startDate: historicalImportForm.startDate,
          endDate: historicalImportForm.endDate,
          includeInsights: historicalImportForm.includeInsights
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Historical import failed');
      }

      toast.success(`Historical import completed: ${result.campaigns_imported} campaigns, ${result.insights_imported} insights imported`);
      setShowHistoricalImport(false);
      await refetch();
    } catch (e: any) {
      toast.error(`Historical import failed: ${e?.message || e}`);
      console.error('Historical import failed:', e);
    } finally {
      setLoading(false);
    }
  };

  // Format last sync time for display
  const formatSyncTime = (time: string | null) => {
    if (!time) return 'Never';
    const date = new Date(time);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.round(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleString();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Megaphone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-2xl font-bold">Campaigns</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={syncWithMeta} disabled={loading} className="btn btn-secondary">Sync with Meta</button>
          <button onClick={refreshData} disabled={loading} className="btn btn-secondary">Refresh Data</button>
          <button 
            onClick={() => setShowHistoricalImport(true)} 
            disabled={loading} 
            className="btn btn-secondary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Historical Import
          </button>
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
            Last synced: {formatSyncTime(lastSyncTime)}
          </div>
        </div>
      </div>

      {campaignsError && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg text-sm">
          Error loading campaigns: {campaignsError}
        </div>
      )}

      {campaignsLoading && (
        <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg text-sm">
          Loading campaigns...
        </div>
      )}

      {!campaignsLoading && !campaignsError && campaigns.length === 0 && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg text-sm">
          No campaigns found. Use "Sync with Meta" to fetch Meta campaigns.
        </div>
      )}

      {/* Filter and Sort Controls */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-4 md:hidden">
          <button 
            className="btn btn-secondary btn-sm flex items-center gap-2"
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707L10 17.414V18a1 1 0 01-2 0v-.586l-1.707-1.707A1 1 0 016 15v-3.586L.293 7.707A1 1 0 010 7V3z" clipRule="evenodd" />
            </svg>
            Filters
          </button>
          <button className="btn btn-secondary btn-sm" onClick={resetFilters}>Reset</button>
        </div>

        <div className={`md:block ${isFilterMenuOpen ? 'block' : 'hidden'}
          transition-all duration-300 ease-in-out`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search campaigns..."
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900"
                value={filterState.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900"
                value={filterState.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min CTR (%)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 1.0"
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900"
                value={filterState.minCTR}
                onChange={(e) => handleFilterChange('minCTR', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date From</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900"
                value={filterState.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date To</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-900"
                value={filterState.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort By:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  className={`btn btn-sm ${sortState.field === 'spend' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSortChange('spend')}
                >
                  Spend {sortState.field === 'spend' && (sortState.order === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  className={`btn btn-sm ${sortState.field === 'ctr' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSortChange('ctr')}
                >
                  CTR {sortState.field === 'ctr' && (sortState.order === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  className={`btn btn-sm ${sortState.field === 'name' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSortChange('name')}
                >
                  Name {sortState.field === 'name' && (sortState.order === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  className={`btn btn-sm ${sortState.field === 'status' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleSortChange('status')}
                >
                  Status {sortState.field === 'status' && (sortState.order === 'asc' ? '↑' : '↓')}
                </button>
              </div>
            </div>
            <button className="btn btn-secondary btn-sm md:mt-0 mt-2" onClick={resetFilters}>Reset Filters & Sorting</button>
          </div>
        </div>
      </div>

      {/* Dashboard Overview Summary Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-4">
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Spend</p>
          <p className="text-2xl font-bold">
            {summary.spend > 0 ? `$${summary.spend.toLocaleString()}` : <span className="text-gray-400 italic">No data</span>}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Impressions</p>
          <p className="text-2xl font-bold">
            {summary.impressions > 0 ? summary.impressions.toLocaleString() : <span className="text-gray-400 italic">No data</span>}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Clicks</p>
          <p className="text-2xl font-bold">
            {summary.clicks > 0 ? summary.clicks.toLocaleString() : <span className="text-gray-400 italic">No data</span>}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">CTR</p>
          <p className="text-2xl font-bold">
            {summary.ctr > 0 ? `${summary.ctr.toFixed(2)}%` : <span className="text-gray-400 italic">No data</span>}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Conversions</p>
          <p className="text-2xl font-bold">
            {summary.conversions > 0 ? summary.conversions.toLocaleString() : <span className="text-gray-400 italic">No data</span>}
          </p>
        </div>
      </div>

      {/* Campaign Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {paginatedCampaigns.length > 0 ? (
          paginatedCampaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onManageAssociations={(campaign) => setSelectedCampaign(campaign)}
              showAssociations={true}
            />
          ))
        ) : (
          <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
            No campaigns found.
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="btn btn-secondary btn-sm"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="btn btn-secondary btn-sm"
          >
            Next
          </button>
        </div>
      )}

      {/* Campaign Associations Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Manage Associations: {selectedCampaign.name}
              </h2>
              <button
                onClick={() => setSelectedCampaign(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <CampaignAssociations
                campaignId={selectedCampaign.id}
                campaignType={selectedCampaign.source || 'meta'}
                campaignName={selectedCampaign.name}
                onAssociationsChange={() => {
                  // Refresh campaign data to update association display
                  refetch();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Historical Import Modal */}
      {showHistoricalImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Historical Data Import
              </h2>
              <button
                onClick={() => setShowHistoricalImport(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={historicalImportForm.startDate}
                    onChange={(e) => setHistoricalImportForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={historicalImportForm.endDate}
                    onChange={(e) => setHistoricalImportForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeInsights"
                    checked={historicalImportForm.includeInsights}
                    onChange={(e) => setHistoricalImportForm(prev => ({ ...prev, includeInsights: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeInsights" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Include performance insights
                  </label>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  This will import all campaigns and their performance data from the selected date range. 
                  Large date ranges may take several minutes to complete.
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowHistoricalImport(false)}
                  className="flex-1 btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleHistoricalImport}
                  disabled={loading || !historicalImportForm.startDate || !historicalImportForm.endDate}
                  className="flex-1 btn btn-primary"
                >
                  {loading ? 'Importing...' : 'Start Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
